import { Card } from '@startuptools/ui/Card';
import { Alert } from '@startuptools/ui/Alert';
import { Button } from '@startuptools/ui/Button';
import { CircularProgress } from '@startuptools/ui/CircularProgress';
import { GenericError } from '@startuptools/ui/Error';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Table } from '@startuptools/ui/Table';
import { IconButton } from '@startuptools/ui/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import { Trans, useLingui } from '@lingui/react';
import { DateInput } from '@startuptools/ui/DateInput';
import {
  useShareholderOverviewSuspenseQuery,
  ShareholderOverviewDocument,
  useDeleteShareholderMutation,
} from './ShareholderOverview.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { ShareholderOverviewTable, type Row } from './ShareholderOverviewTable';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { nosPerClass } from '../utils/nos-per-class';
import { isEmpty, map, maxBy, orderBy, toNumber, uniq, uniqBy } from 'lodash-es';
import { BigNumber } from 'bignumber.js';
import { DateTime } from 'luxon';
import { downloadShareholderOverviewCsv } from '../utils/download-csv';
import { DATE_FORMAT_LUXON } from '@startuptools/common/common';
import { FOOTER_BILINGUAL_STARTUPTOOLS, FOOTER_SV_STARTUPTOOLS } from '../../../document-generation/types';
import { formatIdNumber } from '@startuptools/common/id-number';
import { formatNumber, formatPercent } from '../../../helpers/format-number';
import { PDFButton } from '@startuptools/business-ui/document/PDFPreviewButton';
import { apiClient } from '../../../common/http';
import { type DocGenDetails } from '@startuptools/common/api';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import React from 'react';
import { toast } from '@startuptools/ui/toast/use-toast';

const Component = () => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const [_pending, startTransition] = React.useTransition();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [date, setDate] = useState<DateTime | null>(null);

  const { data, error } = useShareholderOverviewSuspenseQuery({
    variables: { companyId, date: date?.toString() },
    fetchPolicy: 'cache-and-network',
  });

  // eslint-disable-next-line sut/loading-mutation
  const [deleteSh, deleteShState] = useDeleteShareholderMutation({
    refetchQueries: [ShareholderOverviewDocument],
  });

  const showAvatar = useMemo(() => (data.shareState.ownerStates.length ?? 0) <= 50, [data.shareState.ownerStates]);

  const rows = useMemo(() => {
    if (error || !data.shareState.isValid) {
      return [];
    }
    const currentShareholders = data.shareState.ownerStates.filter(s => s.numberOfShares);
    // const totNos = currentShareholders.reduce((sum, sh) => sum + sh.shareState!.numberOfShares, 0);
    const totNos = data.shareState.totalNumberOfShares;
    const sts = currentShareholders.flatMap(os => {
      const nos = nosPerClass(os.ranges);
      return map(nos, (numberOfShares, shareClass) => {
        return {
          identity: os.shareholder.identity,
          email: os.shareholder.identity?.email,
          numberOfShares: numberOfShares.toString(),
          shareClass,
          percent: new BigNumber(numberOfShares).div(totNos).toString(),
        } satisfies Row;
      });
    });
    // Temporary, will implement manual sorting on columns later
    return orderBy(sts, st => toNumber(st.numberOfShares), 'desc');
  }, [data.shareState, error]);

  const handleExportPdf = useCallback(
    async (language: { primaryLang: boolean; secondaryLang: boolean }) => {
      if (!data) {
        throw new Error('Tried to export pdf without data');
      }

      let recordDate: DateTime | null | undefined = date;
      if (!date && data) {
        const dates = data.shareState.ownerStates
          .filter(s => s.numberOfShares)
          .flatMap(s => s.ranges.flatMap(r => DateTime.fromISO(r.transactionDate)));
        recordDate = maxBy(dates, d => d.toMillis());
      }

      const templateData = {
        primary_lang: language.primaryLang,
        secondary_lang: language.secondaryLang,
        company_name: data.company.name,
        organization_number: formatIdNumber(data.company.organizationNumber),
        updated: DateTime.local().toFormat(DATE_FORMAT_LUXON),
        record_date: recordDate?.toFormat(DATE_FORMAT_LUXON),
        include_address: false,
        number_of_share_classes: uniqBy(rows, r => r.shareClass).length,
        states: [
          {
            items: rows.map(row => {
              if (!row.identity) {
                throw new Error('missing identity');
              }
              return {
                name: row.identity.name,
                identity_number: row.identity.isSwedish ? formatIdNumber(row.identity.identityNumber) : '',
                number_of_shares: formatNumber(row.numberOfShares, 0),
                share_class: row.shareClass,
                percentage: formatPercent(row.percent, 2),
              };
            }),
            total_number_of_shares: formatNumber(
              rows.reduce((sum, row) => sum + toNumber(row.numberOfShares), 0),
              0,
            ),
          },
        ],
      };
      const docGenDetails: DocGenDetails = {
        templateId: 'share_register/simple_share_register.html.j2',
        footerPath: templateData.secondary_lang ? FOOTER_BILINGUAL_STARTUPTOOLS : FOOTER_SV_STARTUPTOOLS,
        inputData: templateData,
      };
      const filename = `Aktieägaröversikt_${data.company.name}${date ? '_' + date.toFormat(DATE_FORMAT_LUXON) : ''}`;
      const blob = await apiClient.preview2({
        filename: filename,
        docGens: [{ docGenDetails }],
      });

      return { filename, blob };
    },
    [date, rows, data],
  );

  const handleCopyToClipboard = useCallback(() => {
    const emails = uniq(rows.filter(r => !isEmpty(r.email)).map(r => r.email));
    const bcc = emails.join(';');
    void navigator.clipboard.writeText(bcc).then(() => {
      toast({
        title: <Trans id="Success!" />,
        description: i18n._("Emails were put in your clipboard, paste it in your email client's BCC field"),
        severity: 'success',
      });
    });
  }, [i18n, rows]);

  if (error) {
    return <GenericError />;
  }

  const deleteShareholder = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteSh({ variables: { companyId, id } });
    } catch {
      toast({
        title: <Trans id="Error!" />,
        description: i18n._('Failed to delete shareholder'),
        severity: 'danger',
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Stack direction="column" gap={2}>
        <Card>
          <Typography level="h3">
            <Trans id="Current shareholders" />
          </Typography>
          <Stack direction="row" gap={2} justifyContent="end">
            <PDFButton
              label={i18n.t('Export as PDF')}
              button={({ label, ...props }) => (
                <Button variant="outlined" color="neutral" name={label} disabled={!data.shareState.isValid} {...props}>
                  <Trans id="Export as PDF" />
                </Button>
              )}
              generatePdf={handleExportPdf}
            />
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => downloadShareholderOverviewCsv(data.company.name, rows, date)}
              disabled={!data.shareState.isValid}
            >
              <Trans id="Export as CSV" />
            </Button>
            <Button
              variant="outlined"
              color="neutral"
              onClick={handleCopyToClipboard}
              disabled={!data.shareState.isValid}
            >
              <Trans id="Copy emails" />
            </Button>
            <DateInput
              disabled={!data.shareState.isValid}
              sx={{ width: '210px' }}
              label={<Trans id="Show until date" />}
              value={date}
              onChange={value => {
                startTransition(() => {
                  setDate(value);
                });
              }}
              slotProps={{ field: { clearable: true } }}
            />
          </Stack>
          {data.shareState.isValid ? (
            <ShareholderOverviewTable rows={rows} />
          ) : (
            <Alert color="danger" sx={{ mb: 2 }}>
              <Trans id="The share register is in an unexpected state, but your data is safe. Contact customer support for more info." />
            </Alert>
          )}
        </Card>
        {!date && (
          <Card>
            <Typography level="h3">
              <Trans id="Former shareholders" />
            </Typography>
            <Table noWrap>
              <thead>
                <tr>
                  <th>
                    <Trans id="Name" />
                  </th>
                  <th>
                    <Trans id="Email Address" />
                  </th>
                  <th>
                    <Trans id="Phone Number" />
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.shareState.ownerStates
                  .filter(s => !s.numberOfShares)
                  .map(os => (
                    <tr key={os.shareholderId}>
                      <td>
                        {os.shareholder.identity && (
                          <Identity
                            identity={os.shareholder.identity}
                            showAvatar={showAvatar}
                            updateRefetchQueries={[ShareholderOverviewDocument]}
                          />
                        )}
                      </td>
                      <td>
                        <Typography sx={{ cursor: 'pointer' }}>{os.shareholder.identity?.email}</Typography>
                      </td>
                      <td>
                        <Typography sx={{ cursor: 'pointer' }}>{os.shareholder.identity?.phone}</Typography>
                      </td>
                      <td align="right">
                        <IconButton
                          loading={deleteShState.loading && os.shareholder.id === deletingId}
                          color="danger"
                          onClick={() => {
                            startTransition(() => {
                              void deleteShareholder(os.shareholder.id);
                            });
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Card>
        )}
      </Stack>
    </>
  );
};

export const ShareholderOverview = () => {
  return (
    <Stack gap={2}>
      <Typography level="h2">
        <Trans id="Shareholder Overview" />
      </Typography>
      <Suspense fallback={<CircularProgress />}>
        <ErrorBoundary>
          <Component />
        </ErrorBoundary>
      </Suspense>
    </Stack>
  );
};
